/**
 * Bottom Navigation Component
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Gift, Crown, MessageCircle, User, Settings, Lock, X, Sparkles, Medal, BookOpen } from 'lucide-react';
import { isIOS } from '../utils/platform';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import axios from 'axios';

const API = '/api';

const BottomNav = React.memo(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  // Remember the last non-chat page for "Segnala un problema".
  useEffect(() => {
    if (location.pathname !== '/chat') sessionStorage.setItem('rv_last_page', `${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);
  const [unseenVipCount, setUnseenVipCount] = useState(0);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);

  useEffect(() => {
    if (!user) return;
    const fetchCounts = async () => {
      try {
        const [chatRes, vipRes] = await Promise.all([
          axios.get(`${API}/chat/unread-count`).catch(() => ({ data: { count: 0 } })),
          axios.get(`${API}/vip-predictions/unseen-count`).catch(() => ({ data: { count: 0 } }))
        ]);
        setUnreadChatCount(chatRes.data.count || 0);
        setUnseenVipCount(vipRes.data.count || 0);
      } catch {}
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 60000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    const markAsRead = async () => {
      if (location.pathname === '/chat' && unreadChatCount > 0) {
        try { await axios.post(`${API}/chat/read`); setUnreadChatCount(0); } catch {}
      }
    };
    markAsRead();
  }, [location.pathname, unreadChatCount]);

  useEffect(() => {
    const markVipSeen = async () => {
      if (location.pathname === '/vip' && unseenVipCount > 0) {
        try { await axios.post(`${API}/vip-predictions/mark-seen`); setUnseenVipCount(0); } catch {}
      }
    };
    markVipSeen();
  }, [location.pathname, unseenVipCount]);

  const isPremium = !!(user && (user.is_vip || user.is_gold || user.is_admin));
  const isIOSPlatform = isIOS();
  const flags = useFeatureFlags();
  // Hide Bonus tab when:
  //   • REACT_APP_HIDE_BOOKMAKERS env flag is set at BUILD time (legacy
  //     global kill-switch — useful only if you want to ship a build that
  //     never shows Bonus regardless of server state), OR
  //   • we're on iOS AND the runtime flag `bonus_ios_hidden` is true. The
  //     flag is toggled by the admin from AdminPage → Feature Flags. This
  //     lets us silence the Bonus tab during App Store review windows and
  //     bring it back the moment Apple approves — WITHOUT any rebuild.
  const hideBookmakers =
    process.env.REACT_APP_HIDE_BOOKMAKERS === 'true' ||
    (isIOSPlatform && !!flags.bonus_ios_hidden);

  const navItems = [
    { icon: Home, label: "Home", path: "/dashboard", public: true },
    ...(!hideBookmakers ? [{ icon: Gift, label: "Bonus", path: "/bonus", public: false }] : []),
    // iter248 — Guest gate: changing tab is now a conversion trigger. IA
    // and VIP used to be public (readable without login) because they are
    // marketing-heavy pages; after the Meta Pixel/CAPI redesign we gate
    // them behind registration so we capture CompleteRegistration server
    // -side while the Pixel still has fbc/fbp. Tutorial unlocks the FIRST
    // match click, after that any navigation forces signup.
    { icon: Sparkles, label: "IA", path: "/ia", public: false },
    { icon: BookOpen, label: "Diario", path: "/diario", public: false, premiumOnly: true },
    { icon: Crown, label: "VIP", path: "/vip", public: false, badge: unseenVipCount },
    { icon: Medal, label: "Gold", path: "/gold", public: false, vipOnly: true },
    ...(user?.is_admin ? [{ icon: Settings, label: "Admin", path: "/admin", public: false }] : []),
    { icon: MessageCircle, label: "Chat", path: "/chat", public: false, badge: unreadChatCount },
    { icon: User, label: "Profilo", path: "/profile", public: false },
  ];

  const handleNavClick = (item) => {
    if (!item.public && !user) {
      setShowAuthPrompt(true);
      return;
    }
    // VIP-only tabs: block non-VIP users completely
    if (item.vipOnly && !user?.is_vip) {
      return;
    }
    if (item.premiumOnly && !isPremium) {
      navigate('/vip?da=diario');
      return;
    }
    navigate(item.path);
  };

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-50 max-w-md mx-auto bg-[#1A1A1A]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)', transform: 'translateZ(0)', WebkitTransform: 'translateZ(0)' }}>
        <nav className="bg-[rgba(36,36,36,0.7)] border border-white/[0.08] flex justify-around items-center" style={{ minHeight: '4rem' }} data-testid="bottom-nav">
          {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          const isLocked = !item.public && !user;
          const isVipLocked = user && ((item.vipOnly && !user.is_vip) || (item.premiumOnly && !isPremium));
          return (
            <button
              key={item.path}
              onClick={() => handleNavClick(item)}
              className={`flex flex-col items-center gap-1 py-2 px-0.5 transition-all relative ${
                isActive ? "text-[#F5A623]" : (isLocked || isVipLocked) ? "text-gray-600" : "text-gray-500 hover:text-gray-300"
              }`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <div className="relative">
                <item.icon className={`w-5 h-5 ${isActive ? 'drop-shadow-[0_0_8px_rgba(245,166,35,0.5)]' : ''}`} />
                {(isLocked || isVipLocked) && (
                  <Lock className="w-2.5 h-2.5 text-gray-500 absolute -bottom-0.5 -right-1" />
                )}
                {item.badge > 0 && !isLocked && (
                  <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 animate-pulse" data-testid={`badge-${item.label.toLowerCase()}`}>
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </div>
              <span className="text-[9px] font-bold uppercase tracking-wide">{item.label}</span>
            </button>
          );
        })}
        </nav>
      </div>

      {/* Auth Required Popup */}
      {showAuthPrompt && (
        <>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]" onClick={() => setShowAuthPrompt(false)} />
          <div className="fixed inset-x-0 bottom-0 z-[9999] px-4 pb-24 animate-slide-up" data-testid="auth-prompt-modal">
            <div className="max-w-md mx-auto glass rounded-3xl p-6 border border-white/10">
              <button onClick={() => setShowAuthPrompt(false)} className="absolute top-4 right-4 text-gray-500 hover:text-white" data-testid="close-auth-prompt">
                <X className="w-5 h-5" />
              </button>
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#F5A623]/20 flex items-center justify-center">
                  <Lock className="w-8 h-8 text-[#F5A623]" />
                </div>
                <h3 className="text-xl font-black text-white mb-2" style={{fontFamily: 'Barlow Condensed'}}>
                  ACCESSO RICHIESTO
                </h3>
                <p className="text-gray-400 text-sm mb-6">
                  Per accedere a quest'area, registrati o effettua il login
                </p>
                <button
                  onClick={() => { setShowAuthPrompt(false); navigate('/login'); }}
                  className="w-full h-14 btn-primary rounded-xl font-bold text-base"
                  data-testid="auth-prompt-login-btn"
                >
                  ACCEDI O REGISTRATI
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
});

export default BottomNav;
